import { Mail, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/Button';
import { CreateCampaignDialog } from '@/components/CreateCampaignDialog';
import { CampaignStatusPill, Pill } from '@/components/StatusPill';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/States';
import { useCampaigns } from '@/hooks/use-campaigns';
import type { CampaignStatus, CampaignSummary } from '@/lib/schemas';
import { formatDateTime, formatRelative } from '@/lib/utils';
import { cn } from '@/lib/utils';

const statusFilters: (CampaignStatus | 'all')[] = [
  'all',
  'Draft',
  'InReview',
  'Approved',
  'Scheduled',
  'Sending',
  'Sent',
];

/**
 * The campaign list. Composition only — it wires the hook to the presentational pieces and owns
 * nothing but its own filter state, which is genuinely local UI state.
 */
export function CampaignsPage() {
  const [status, setStatus] = useState<CampaignStatus | 'all'>('all');
  const [search, setSearch] = useState('');

  /**
   * `?new=1` opens the dialog, which is how the nav rail's New campaign action reaches it from any
   * page. The dialog stays owned by this page — two owners of one dialog is how you get two of them
   * open — so the rail navigates here and the param is the handoff. Closing clears it, or the
   * back button would reopen the dialog on a page the user had just dismissed it from.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const isCreating = searchParams.get('new') !== null;

  function setCreating(open: boolean) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);

        if (open) next.set('new', '1');
        else next.delete('new');

        return next;
      },
      { replace: true },
    );
  }

  const filters = {
    ...(status === 'all' ? {} : { status }),
    ...(search.length > 0 ? { query: search } : {}),
  };

  const { data, isPending, isError, error, refetch } = useCampaigns(filters);

  return (
    <div className="px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-heading">Campaigns</h1>
          <p className="mt-0.5 text-sm text-muted">
            Create, review and send email campaigns.
          </p>
        </div>

        {/* One primary action per page. */}
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          New campaign
        </Button>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          {/* A label, not a placeholder alone — a placeholder vanishes on focus, fails contrast
              and is invisible to a screen reader. Visually hidden because the magnifier icon plus
              the page context makes it obvious to a sighted user. */}
          <label htmlFor="campaign-search" className="sr-only">
            Search campaigns by name
          </label>

          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
            aria-hidden="true"
          />

          <input
            id="campaign-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search campaigns"
            className={cn(
              'h-10 w-full rounded-md border border-border bg-surface pl-9 pr-3 text-sm',
              'text-foreground placeholder:text-muted',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
            )}
          />
        </div>

        <div
          className="flex flex-wrap items-center gap-1 rounded-md bg-surface-2 p-0.5"
          role="tablist"
          aria-label="Filter by status"
        >
          {statusFilters.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={status === value}
              onClick={() => setStatus(value)}
              className={cn(
                'rounded px-2.5 py-1.5 text-xs font-medium',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                status === value
                  ? 'bg-surface text-foreground shadow-sm'
                  : 'text-muted hover:text-foreground',
              )}
            >
              {value === 'all' ? 'All' : value === 'InReview' ? 'In review' : value}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-border bg-surface">
        {isPending ? (
          <TableSkeleton rows={6} />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={Mail}
            title={
              search.length > 0 || status !== 'all'
                ? 'No campaigns match those filters'
                : 'No campaigns yet'
            }
            description={
              search.length > 0 || status !== 'all'
                ? 'Try a different status or clear the search.'
                : 'A campaign holds the HTML, the subject and the audience. Create one to start authoring.'
            }
            action={
              search.length > 0 || status !== 'all' ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setSearch('');
                    setStatus('all');
                  }}
                >
                  Clear filters
                </Button>
              ) : (
                <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  New campaign
                </Button>
              )
            }
          />
        ) : (
          <CampaignTable campaigns={data.items} total={data.total} />
        )}
      </div>

      {isCreating && <CreateCampaignDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/**
 * Below 768px this becomes a card list rather than a horizontally scrolling table.
 *
 * A scrolling table is acceptable only when the scroll is discoverable; six columns on a phone is
 * not, and silently clipping the last column — which here is the one saying when it was last
 * edited — is not an option.
 */
function CampaignTable({ campaigns, total }: { campaigns: CampaignSummary[]; total: number }) {
  return (
    <>
      {/* Desktop */}
      <table className="hidden w-full text-sm md:table">
        <caption className="sr-only">
          {total} campaign{total === 1 ? '' : 's'}
        </caption>

        <thead className="bg-surface-2 text-left text-xs font-medium text-muted">
          <tr>
            <th scope="col" className="px-4 py-2.5">
              Campaign
            </th>
            <th scope="col" className="px-4 py-2.5">
              Status
            </th>
            <th scope="col" className="px-4 py-2.5">
              Brand
            </th>
            {/* Numbers right-aligned; text left. Never centre either. */}
            <th scope="col" className="px-4 py-2.5 text-right">
              Audience
            </th>
            <th scope="col" className="px-4 py-2.5 text-right">
              Variants
            </th>
            <th scope="col" className="px-4 py-2.5">
              Last edited
            </th>
          </tr>
        </thead>

        <tbody>
          {campaigns.map((campaign) => (
            <tr key={campaign.id} className="border-t border-border hover:bg-row-hover">
              <td className="px-4 py-3">
                <Link
                  to={`/campaigns/${campaign.id}`}
                  className="font-medium text-link underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  {campaign.name}
                </Link>

                {campaign.armCount > 1 && (
                  <Pill tone="info" className="ml-2">
                    {campaign.armCount} arms
                  </Pill>
                )}
              </td>

              <td className="px-4 py-3">
                <CampaignStatusPill status={campaign.status} />
              </td>

              <td className="px-4 py-3 text-muted">{campaign.brandName}</td>

              <td className="px-4 py-3 text-right tabular-nums text-muted">
                {campaign.audienceSize ?? '—'}
              </td>

              <td className="px-4 py-3 text-right tabular-nums text-muted">
                {campaign.variantCount ?? '—'}
              </td>

              <td className="px-4 py-3 text-muted">
                <time dateTime={campaign.updatedAt} title={formatDateTime(campaign.updatedAt)}>
                  {formatRelative(campaign.updatedAt)}
                </time>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile */}
      <ul className="divide-y divide-border md:hidden">
        {campaigns.map((campaign) => (
          <li key={campaign.id} className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <Link
                to={`/campaigns/${campaign.id}`}
                className="font-medium text-link underline-offset-2 hover:underline"
              >
                {campaign.name}
              </Link>

              <CampaignStatusPill status={campaign.status} />
            </div>

            <p className="mt-1 text-xs text-muted">
              {campaign.brandName} · {campaign.audienceSize ?? 0} recipients ·{' '}
              {formatRelative(campaign.updatedAt)}
            </p>
          </li>
        ))}
      </ul>
    </>
  );
}
